import {
  Key, KeyRound, Wallet, Cpu, HardDrive, Smartphone, Laptop, Tablet, Headphones,
  BatteryCharging, ShoppingBag, Backpack, Briefcase, Shirt, Footprints, Glasses, Gem,
  Watch, FileText, CreditCard, Book, BookOpen, Gamepad2, Umbrella, Music, Dumbbell,
  Stethoscope, PawPrint, Camera, Banknote, Contact, Speaker, Flashlight, Bike,
  Baby, Mouse, Palette, Pill, Gift, Package, MoreHorizontal,
} from "lucide-react";

// The active translator is registered by LanguageContext so these plain helpers
// (used across ~30 call sites) stay localized without threading `t` everywhere.
// Fallback returns the i18n key itself — never a hardcoded French string.
let _t = (key) => key;
export const registerCategoryTranslator = (fn) => { if (typeof fn === "function") _t = fn; };

// Single source of truth — keys MUST match backend CATEGORIES list.
const KEYS = [
  "Animal", "AppareilPhoto", "Argent", "Bijoux", "CarteBancaire", "CarteIdentite",
  "Casquette", "Ceinture", "Chargeur", "Chaussures", "Cles", "CleVoiture", "Console",
  "Documents", "Echarpe", "Ecouteurs", "Electronique", "Enceinte", "Gants", "Gourde",
  "Instrument", "Jouet", "Jumelles", "Lampe", "Liseuse", "Livre", "Lunettes", "Manteau",
  "Maquillage", "Medical", "Medicaments", "Montre", "Ordinateur", "Parapluie",
  "Portefeuille", "Poussette", "Sac", "SacADos", "Souris", "Sport", "Tablette",
  "Telephone", "Trottinette", "Trousse", "USB", "Valise", "Velo", "Vetements",
];

const ICONS = {
  Animal: PawPrint, AppareilPhoto: Camera, Argent: Banknote, Bijoux: Gem,
  CarteBancaire: CreditCard, CarteIdentite: Contact, Casquette: Package, Ceinture: Package,
  Chargeur: BatteryCharging, Chaussures: Footprints, Cles: Key, CleVoiture: KeyRound,
  Console: Gamepad2, Documents: FileText, Echarpe: Package, Ecouteurs: Headphones,
  Electronique: Cpu, Enceinte: Speaker, Gants: Package, Gourde: Package, Instrument: Music,
  Jouet: Gift, Jumelles: Package, Lampe: Flashlight, Liseuse: BookOpen, Livre: Book,
  Lunettes: Glasses, Manteau: Shirt, Maquillage: Palette, Medical: Stethoscope,
  Medicaments: Pill, Montre: Watch, Ordinateur: Laptop, Parapluie: Umbrella,
  Portefeuille: Wallet, Poussette: Baby, Sac: ShoppingBag, SacADos: Backpack, Souris: Mouse,
  Sport: Dumbbell, Tablette: Tablet, Telephone: Smartphone, Trottinette: Package,
  Trousse: Package, USB: HardDrive, Valise: Briefcase, Velo: Bike, Vetements: Shirt,
  Autres: MoreHorizontal,
};

// Order kept stable by key; CategorySelect re-sorts by the translated label so
// alphabetical order follows the active language.
export const CATEGORIES = [
  ...KEYS.map((key) => ({ key, Icon: ICONS[key], get label() { return _t(`cat.${key}`); } })),
  { key: "Autres", Icon: MoreHorizontal, get label() { return _t("cat.Autres"); } },
];

export const labelFor = (key) => {
  if (!key) return "";
  return ICONS[key] ? _t(`cat.${key}`) : key;
};
export const IconFor = (key) => ICONS[key] || MoreHorizontal;

// Distinct accent color per category (used by the category selector icons).
const COLORS = {
  Animal: "#B45309", AppareilPhoto: "#7C3AED", Argent: "#16A34A", Bijoux: "#DB2777",
  CarteBancaire: "#2563EB", CarteIdentite: "#0891B2", Casquette: "#CA8A04", Ceinture: "#92400E",
  Chargeur: "#059669", Chaussures: "#9333EA", Cles: "#F59E0B", CleVoiture: "#D97706",
  Console: "#4F46E5", Documents: "#64748B", Echarpe: "#E11D48", Ecouteurs: "#0EA5E9",
  Electronique: "#6366F1", Enceinte: "#8B5CF6", Gants: "#A16207", Gourde: "#0D9488",
  Instrument: "#C026D3", Jouet: "#EA580C", Jumelles: "#0F766E", Lampe: "#EAB308",
  Liseuse: "#0284C7", Livre: "#B91C1C", Lunettes: "#4338CA", Manteau: "#BE123C",
  Maquillage: "#EC4899", Medical: "#DC2626", Medicaments: "#F43F5E", Montre: "#7C2D12",
  Ordinateur: "#1D4ED8", Parapluie: "#0E7490", Portefeuille: "#A16207", Poussette: "#DB2777",
  Sac: "#9333EA", SacADos: "#6D28D9", Souris: "#475569", Sport: "#22C55E", Tablette: "#3B82F6",
  Telephone: "#0EA5E9", Trottinette: "#F97316", Trousse: "#D97706", USB: "#10B981",
  Valise: "#7C2D12", Velo: "#15803D", Vetements: "#BE185D", Autres: "#64748B",
};
export const colorFor = (key) => COLORS[key] || "#64748B";

// Emoji per category (displayed at >=28px in the category selector).
const EMOJIS = {
  Animal: "🐾", AppareilPhoto: "📷", Argent: "💰", Bijoux: "💍", CarteBancaire: "💳",
  CarteIdentite: "🪪", Casquette: "🧢", Ceinture: "🪢", Chargeur: "🔌", Chaussures: "👟",
  Cles: "🔑", CleVoiture: "🗝️", Console: "🎮", Documents: "📄", Echarpe: "🧣",
  Ecouteurs: "🎧", Electronique: "⚙️", Enceinte: "🔊", Gants: "🧤", Gourde: "🍶",
  Instrument: "🎸", Jouet: "🧸", Jumelles: "🔭", Lampe: "🔦", Liseuse: "📖", Livre: "📚",
  Lunettes: "👓", Manteau: "🧥", Maquillage: "💄", Medical: "🩺", Medicaments: "💊",
  Montre: "⌚", Ordinateur: "💻", Parapluie: "☂️", Portefeuille: "👛", Poussette: "👶",
  Sac: "👜", SacADos: "🎒", Souris: "🖱️", Sport: "🏋️", Tablette: "📱", Telephone: "☎️",
  Trottinette: "🛴", Trousse: "✏️", USB: "💾", Valise: "🧳", Velo: "🚲", Vetements: "👕",
  Autres: "📦",
};
export const emojiFor = (key) => EMOJIS[key] || "📦";

const STATUS_CLS = {
  in_storage: "bg-slate-100 text-slate-700",
  matched: "bg-amber-100 text-amber-800",
  paid: "bg-teal-100 text-teal-800",
  shipping_requested: "bg-blue-100 text-blue-800",
  shipped: "bg-blue-100 text-blue-800",
  restituted: "bg-emerald-100 text-emerald-800",
  destroyed: "bg-rose-100 text-rose-800",
  donated: "bg-violet-100 text-violet-800",
  auctioned: "bg-cyan-100 text-cyan-800",
  handed_to_authorities: "bg-slate-200 text-slate-800",
  pending: "bg-amber-100 text-amber-800",
  submitted: "bg-amber-100 text-amber-800",
  open: "bg-amber-100 text-amber-800",
  cancelled_by_customer: "bg-slate-200 text-slate-700",
};
export const statusLabel = (s) => (s && STATUS_CLS[s] ? _t(`st.${s}`) : (s || ""));
export const statusCls = (s) => STATUS_CLS[s] || "bg-slate-100 text-slate-700";
